import { Link } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { AdminCard, EmptyInline, MetricCard } from "../components/dashboard";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Automations — rules ODIN runs on your server. Layout uses the ODIN design
 * system; the stat row and cards are driven by real data with honest empty
 * states until a server is connected. No fabricated automations or run history.
 */

export function Automations(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Automations</h1>
            <p className="ax-head__sub">Rules and workflows ODIN runs on your server, with a full run history.</p>
          </div>
          <Link to="/app/servers" className="ax-btn ax-btn--secondary">
            {connected ? "New automation" : "Connect a server"}
          </Link>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <>
            <div className="ax-stats4">
              <MetricCard label="Active" icon={<Icon.automations />} iconTone="success" value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Paused" icon={<Icon.settings />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Runs (24h)" icon={<Icon.arrowUp />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Failures (24h)" icon={<Icon.moderation />} iconTone="error" value={null} foot={connected ? "Awaiting sync" : "No server"} />
            </div>

            <div className="ax-sections">
              <AdminCard title="Automations">
                <EmptyInline title="No automations yet">
                  {connected
                    ? "Create an automation to run rules and workflows on your server."
                    : "Connect a Discord server to build automations."}
                </EmptyInline>
              </AdminCard>

              <AdminCard title="Recent runs">
                <EmptyInline title="No runs recorded">
                  {connected
                    ? "Run history appears here once an automation executes."
                    : "Connect a Discord server to see automation runs."}
                </EmptyInline>
              </AdminCard>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
